import { Task } from "fhir/r5";
import dayjs from "dayjs";
import { useAxios } from "../../../utils/useAxios";
import { useAuth } from "../../../utils/useAuth";
import { endpoints } from "../../../configs/Settings";
import { toast } from "react-toastify";
import { GetPatient } from "../../../services/fhir";

/**
 * Interface para os dados necessários para criar uma notificação de documento
 */
interface IDocumentTaskNotificationProps {
  organization?: string;
  practitionerId?: string;
  patientId?: string;
  patientName?: string;
  medicationName?: string;
  title?: string;
  description?: string;
}

/**
 * Cria uma tarefa de notificação para um documento
 * @param props - Dados do documento
 * @returns Promise com o resultado da criação da notificação
 */
export async function createDocumentTaskNotification(
  props: IDocumentTaskNotificationProps
): Promise<Task | null> {
  try {
    // Verifica se os dados necessários estão presentes
    if (!props.patientId || !props.practitionerId) {
      console.warn("Dados insuficientes para criar notificação");
      return null;
    }

    // Cria a referência do paciente
    const patientReference = `Patient/2.16.840.1.113883.13.237-${props.patientId}`;

    // Cria a referência do profissional
    const practitionerReference = `PractitionerRole/${props?.organization}-2.16.840.1.113883.13.237-${props.practitionerId}`;

    // Prepara a descrição com o nome do paciente e do medicamento, se disponíveis
    let description = props.description || "Documento adicionado ao prontuário";

    // Se temos o nome do paciente e do medicamento, criamos uma descrição personalizada
    if (props.patientName && props.medicationName) {
      description = `Paciente ${props.patientName} interagiu com o medicamento ${props.medicationName}`;
    } else if (props.patientName) {
      description = `Paciente ${props.patientName} adicionou um novo documento`;
    }

    // Cria o objeto Task no formato FHIR R5
    const taskData = {
      resourceType: "Task",
      status: "requested",
      intent: "order",
      priority: "routine",
      code: {
        text: props.title,
        coding: [
          {
            system: "https://fluxmed.com.br/fhir/CodeSystem/task-types",
            code: "NOTIFICATION_DOCUMENT",
          },
        ],
      },
      description: description,
      for: {
        reference: practitionerReference,
      },
      owner: {
        reference: patientReference,
      },
      authoredOn: dayjs().toISOString(),
    };

    // Envia a tarefa para o servidor
    const url = `${endpoints.WORKFLOWFHIR}/Task`;
    const headers = {
      Accept: "*/*",
      Authorization: await useAuth.getUserToken(),
      "Content-Type": "application/fhir+json",
    };

    const response = await useAxios.post(url, JSON.stringify(taskData), {
      headers,
      timeout: 20000,
    });

    console.log("Notificação criada com sucesso:", response.data);
    return response.data;
  } catch (error) {
    console.error("Erro ao criar notificação:", error);
    return null;
  }
}

/**
 * Hook para utilizar a funcionalidade de notificação de documentos
 * @returns Objeto com funções relacionadas a notificações de documentos
 */
export function useDocumentTaskNotification() {
  /**
   * Cria uma notificação para um documento
   * @param props - Dados necessários para criar a notificação
   * @returns Promise com o resultado da criação da notificação
   */
  const createNotification = async (props: IDocumentTaskNotificationProps) => {
    try {
      const result = await createDocumentTaskNotification(props);

      if (result) {
        toast.success("Notificação criada com sucesso");
      }

      return result;
    } catch (error) {
      console.error("Erro ao criar notificação:", error);
      toast.error("Erro ao criar notificação");
      return null;
    }
  };

  return {
    createNotification,
  };
}
